import { useMemo } from 'react';

import { getSearchTextMatchPriority } from '@kc-console/core';

import { createCache } from './utils';

import type { OptionKey, UseOptionsProps, UseOptionsReturn } from './types';

export default function useOptions<Option = unknown>(
  props: UseOptionsProps<Option>,
): UseOptionsReturn<Option> {
  const {
    options,
    getOptionKey: getOptionKeyProp,
    getOptionGroup: getOptionGroupProp,
    getOptionDisabled: getOptionDisabledProp,
    getOptionSelected: getOptionSelectedProp,
    getOptionLabel: getOptionLabelProp,
    getOptionFiltered: getOptionFilteredProp,
  } = props;

  const getOptionIndex = useMemo(() => {
    const indexMap = options.reduce(
      (map, option, optionIndex) => map.set(option, optionIndex),
      new Map<Option, number>(),
    );
    return (option: Option) => indexMap.get(option) ?? -1;
  }, [options]);

  const getOptionKey = useMemo(() => {
    const cache = createCache<Option, OptionKey>();
    const getOptionKey = getOptionKeyProp ?? getOptionIndex;
    const getContext = (option: Option) => ({
      optionIndex: getOptionIndex(option),
    });
    return (option: Option) => {
      return cache.use(option, () => getOptionKey(option, getContext(option)));
    };
  }, [getOptionKeyProp, getOptionIndex]);

  const getOptionGroup = useMemo(() => {
    const cache = createCache<Option, string | undefined>();
    const getOptionGroup = getOptionGroupProp ?? (() => undefined);
    const getContext = (option: Option) => ({
      optionIndex: getOptionIndex(option),
      key: getOptionKey(option),
    });
    return (option: Option) => {
      return cache.use(option, () => getOptionGroup(option, getContext(option)));
    };
  }, [getOptionGroupProp, getOptionIndex, getOptionKey]);

  const getOptionDisabled = useMemo(() => {
    const cache = createCache<Option, boolean>();
    const getOptionDisabled = getOptionDisabledProp ?? (() => false);
    const getContext = (option: Option) => ({
      optionIndex: getOptionIndex(option),
      key: getOptionKey(option),
      group: getOptionGroup(option),
    });
    return (option: Option) => {
      return cache.use(option, () => !!getOptionDisabled(option, getContext(option)));
    };
  }, [getOptionDisabledProp, getOptionIndex, getOptionKey, getOptionGroup]);

  const getOptionSelected = useMemo(() => {
    const cache = createCache<Option, boolean>();
    const getOptionSelected = getOptionSelectedProp ?? (() => false);
    const getContext = (option: Option) => ({
      optionIndex: getOptionIndex(option),
      key: getOptionKey(option),
      group: getOptionGroup(option),
    });
    return (option: Option) => {
      return cache.use(option, () => !!getOptionSelected(option, getContext(option)));
    };
  }, [getOptionSelectedProp, getOptionIndex, getOptionKey, getOptionGroup]);

  const getOptionLabel = useMemo(() => {
    const cache = createCache<Option, string>();
    const getOptionLabel = getOptionLabelProp ?? ((option: Option): string => String(option));
    const getContext = (option: Option) => ({
      optionIndex: getOptionIndex(option),
      key: getOptionKey(option),
      disabled: getOptionDisabled(option),
      selected: getOptionSelected(option),
      group: getOptionGroup(option),
    });
    return (option: Option) => {
      return cache.use(option, () => getOptionLabel(option, getContext(option)));
    };
  }, [
    getOptionLabelProp,
    getOptionIndex,
    getOptionKey,
    getOptionDisabled,
    getOptionSelected,
    getOptionGroup,
  ]);

  const getOptionFiltered = useMemo(() => {
    const cache = createCache<Option, boolean>();
    const getOptionFiltered =
      getOptionFilteredProp ??
      ((_option: Option, context: { label: string; search: string }) =>
        Number.isFinite(getSearchTextMatchPriority(context.label, x => x, context.search)));
    const getContext = (option: Option) => ({
      optionIndex: getOptionIndex(option),
      key: getOptionKey(option),
      disabled: getOptionDisabled(option),
      selected: getOptionSelected(option),
      label: getOptionLabel(option),
      group: getOptionGroup(option),
    });
    return (option: Option, context: { search: string }) => {
      return cache.use(
        option,
        () => !!getOptionFiltered(option, { ...getContext(option), ...context }),
      );
    };
  }, [
    getOptionFilteredProp,
    getOptionIndex,
    getOptionKey,
    getOptionDisabled,
    getOptionSelected,
    getOptionLabel,
    getOptionGroup,
  ]);

  const groupedOptions: { group: string; options: Option[] }[] = useMemo(() => {
    if (!getOptionGroupProp) {
      return [];
    }
    return options.reduce(
      (groups, option) => {
        const group = getOptionGroup(option);

        if (group) {
          const exists = groups.find(group2 => group2.group === group);
          if (exists) {
            exists.options.push(option);
          } else {
            groups.push({ group, options: [option] });
          }
        }

        return groups;
      },
      [] as { group: string; options: Option[] }[],
    );
  }, [options, getOptionGroupProp, getOptionGroup]);

  const result: UseOptionsReturn<Option> = {
    options,
    groupedOptions,
    getOptionIndex,
    getOptionKey,
    getOptionDisabled,
    getOptionSelected,
    getOptionLabel,
    getOptionFiltered,
    getOptionGroup,
  };
  return result;
}
